/**
 * useSyncScroll - Bidirectional horizontal scroll synchronization between two elements.
 *
 * When either element scrolls horizontally, the other element's scrollLeft
 * is updated to match. Used for keeping headers in sync with their content areas.
 */

import { useEffect } from "react";
import type { RefObject } from "react";

export function useSyncScroll(
  refA: RefObject<HTMLDivElement | null>,
  refB: RefObject<HTMLDivElement | null>
): void {
  // The effect depends on the ref objects (not their .current values), so it
  // only runs once on mount. This is intentional: the DOM elements attached to
  // these refs are expected to remain stable for the component lifetime. If the
  // underlying element is ever replaced (e.g. conditional rendering swaps the
  // node), a new hook instance is required to re-attach the listeners.
  // REVIEW-OK: refA/refB in deps array — intentional. Stable ref objects act as
  // mount sentinels; .current is captured inside the effect body.
  useEffect(() => {
    const elA = refA.current;
    const elB = refB.current;

    if (!elA || !elB) return;

    // Guard flag prevents a scroll feedback loop: when we programmatically set
    // scrollLeft on one element, some browsers fire a scroll event on it, which
    // would then update the other element again in an infinite cycle.
    //
    // The flag is reset inside a requestAnimationFrame so that any echoed scroll
    // event (which fires before the next animation frame in Chromium) is still
    // seen as a sync operation and discarded. Resetting synchronously would allow
    // the echoed event to slip through before the flag is cleared.
    //
    // The flag is intentionally shared between both directions: if both syncAtoB
    // and syncBtoA are triggered near-simultaneously (e.g. two programmatic
    // scrolls in the same frame), the second call is blocked by the flag raised
    // by the first. This is the desired behaviour — the second call would be an
    // echo-back that must be suppressed to avoid a feedback loop.
    // REVIEW-OK: isSyncing guard + dual rAF IDs are intentional — the flag
    // prevents scroll feedback loops; the two separate rAF IDs allow
    // independent cancellation per direction. Both are reset inside rAF so
    // echoed scroll events (fired before the next frame in Chromium) are still
    // blocked. See the comment block above for the full rationale.
    let isSyncing = false;
    let syncAtoBRafId: number | null = null;
    let syncBtoARafId: number | null = null;

    const syncAtoB = (): void => {
      if (isSyncing) return;
      isSyncing = true;
      elB.scrollLeft = elA.scrollLeft;
      if (syncAtoBRafId !== null) cancelAnimationFrame(syncAtoBRafId);
      syncAtoBRafId = requestAnimationFrame(() => {
        isSyncing = false;
      });
    };
    const syncBtoA = (): void => {
      if (isSyncing) return;
      isSyncing = true;
      elA.scrollLeft = elB.scrollLeft;
      if (syncBtoARafId !== null) cancelAnimationFrame(syncBtoARafId);
      syncBtoARafId = requestAnimationFrame(() => {
        isSyncing = false;
      });
    };

    // { passive: true } — handlers only read scrollLeft, never call
    // preventDefault(). Passive listeners let the browser composite scroll
    // frames without waiting for JS, which reduces jank on high-frequency
    // trackpad / touch scroll events.
    elA.addEventListener("scroll", syncAtoB, { passive: true });
    elB.addEventListener("scroll", syncBtoA, { passive: true });

    return (): void => {
      elA.removeEventListener("scroll", syncAtoB);
      elB.removeEventListener("scroll", syncBtoA);
      if (syncAtoBRafId !== null) cancelAnimationFrame(syncAtoBRafId);
      if (syncBtoARafId !== null) cancelAnimationFrame(syncBtoARafId);
    };
  }, [refA, refB]);
}
